import { supabase } from '../lib/supabase';

export interface CalendarBlock {
  id: string;
  date: string; // YYYY-MM-DD
  reason: string | null;
  created_at: string;
  contract_id?: string | null;
  session_id?: string | null;
  block_type?: string | null;
  is_blocked?: boolean;
}

/**
 * Normaliza el campo date a formato YYYY-MM-DD sin importar cómo lo devuelva Supabase
 */
function normalizeDate(dateValue: string): string {
  if (!dateValue) return dateValue;
  // Si viene como "2026-02-07T00:00:00+00:00" o similar, tomar solo los primeros 10 chars
  return dateValue.substring(0, 10);
}

function normalizBlock(block: any): CalendarBlock {
  return {
    ...block,
    date: normalizeDate(block.date),
  };
}

/**
 * Obtiene la fecha actual en formato YYYY-MM-DD (zona horaria local del navegador)
 */
function getTodayString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Obtiene todos los bloqueos de calendario (solo los activos)
 */
export async function getCalendarBlocks(): Promise<CalendarBlock[]> {
  try {
    const { data, error } = await supabase
      .from('calendar_blocks')
      .select('*')
      .order('date', { ascending: true });

    if (error) {
      console.error('❌ Error fetching calendar blocks:', error);
      if (error.code === '42P01' || error.code === 'PGRST301') {
        return [];
      }
      throw error;
    }

    return (data ?? []).map(normalizBlock);
  } catch (err) {
    console.error('❌ Error in getCalendarBlocks:', err);
    return [];
  }
}

/**
 * Obtiene los bloqueos de un mes específico
 */
export async function getBlocksForMonth(year: number, month: number): Promise<CalendarBlock[]> {
  try {
    const endDate = new Date(year, month + 1, 0);
    const startDateStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const endDay = endDate.getDate();
    const endDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;

    console.log(`🔍 Obteniendo bloques para ${year}-${month + 1} (${startDateStr} a ${endDateStr})`);

    const { data, error } = await supabase
      .from('calendar_blocks')
      .select('*')
      .gte('date', startDateStr)
      .lte('date', endDateStr)
      .order('date', { ascending: true });

    if (error) {
      console.error('❌ Error fetching blocks for month:', error);
      if (error.code === '42P01' || error.code === 'PGRST301') {
        return [];
      }
      throw error;
    }

    const normalized = (data ?? []).map(normalizBlock);
    console.log(`✅ Bloques del mes obtenidos: ${normalized.length}`, normalized);
    return normalized;
  } catch (err) {
    console.error('❌ Error in getBlocksForMonth:', err);
    return [];
  }
}

/**
 * Obtiene los próximos N bloqueos desde hoy
 */
export async function getUpcomingBlocks(limit: number = 5): Promise<CalendarBlock[]> {
  try {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const { data, error } = await supabase
      .from('calendar_blocks')
      .select('*')
      .gte('date', today)
      .order('date', { ascending: true })
      .limit(limit);

    if (error) {
      console.error('❌ Error fetching upcoming blocks:', error);
      if (error.code === '42P01' || error.code === 'PGRST301') {
        return [];
      }
      throw error;
    }

    const normalized = (data ?? []).map(normalizBlock);
    console.log(`✅ Próximos bloques: ${normalized.length}`, normalized);
    return normalized;
  } catch (err) {
    console.error('❌ Error in getUpcomingBlocks:', err);
    return [];
  }
}

/**
 * Obtiene bloqueos que vienen de contratos (para mostrar en calendario)
 */
export async function getContractBlocks(): Promise<CalendarBlock[]> {
  try {
    const { data, error } = await supabase
      .from('calendar_blocks')
      .select('*')
      .not('contract_id', 'is', null)
      .order('date', { ascending: true });

    if (error) {
      console.error('❌ Error fetching contract blocks:', error);
      return [];
    }

    return (data ?? []).map(normalizBlock);
  } catch (err) {
    console.error('❌ Error in getContractBlocks:', err);
    return [];
  }
}

/**
 * Verifica si una fecha está disponible (no bloqueada)
 */
export async function isDateAvailable(date: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('calendar_blocks')
      .select('id')
      .eq('date', date)
      .maybeSingle();

    if (error) {
      console.error('❌ Error checking date availability:', error);
      return true;
    }

    return !data;
  } catch (err) {
    console.error('❌ Error in isDateAvailable:', err);
    return true;
  }
}

/**
 * Verifica si una fecha está bloqueada (por contrato o manualmente)
 */
export async function isDateBlocked(date: string): Promise<{ blocked: boolean; reason?: string; contractId?: string }> {
  try {
    const { data, error } = await supabase
      .from('calendar_blocks')
      .select('*')
      .eq('date', date)
      .maybeSingle();

    if (error) {
      console.error('❌ Error checking blocked date:', error);
      return { blocked: false };
    }

    if (!data) return { blocked: false };

    return {
      blocked: true,
      reason: data.reason || 'Fecha no disponible',
      contractId: data.contract_id || undefined,
    };
  } catch (err) {
    console.error('❌ Error in isDateBlocked:', err);
    return { blocked: false };
  }
}

/**
 * Bloquea una fecha en el calendario
 */
export async function blockDate(date: string, reason?: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('calendar_blocks')
      .insert({
        date,
        reason: reason || null,
      });

    if (error) {
      console.error('❌ Error blocking date:', error);
      throw new Error('Error al bloquear la fecha');
    }
  } catch (err) {
    console.error('❌ Error in blockDate:', err);
    throw err;
  }
}

/**
 * Desbloquea una fecha del calendario
 */
export async function unblockDate(dateOrId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('calendar_blocks')
      .delete()
      .eq('id', dateOrId);

    if (error) {
      console.error('❌ Error unblocking date:', error);
      throw new Error('Error al desbloquear la fecha');
    }
  } catch (err) {
    console.error('❌ Error in unblockDate:', err);
    throw err;
  }
}

/**
 * TEST: Insertar una fecha de prueba para diagnóstico
 */
export async function insertTestDate(): Promise<void> {
  console.log('🧪 Insertando fecha de prueba...');
  
  const testDate = '2026-01-28';
  const testReason = 'TEST - Diagnóstico Readdy';
  
  try {
    const { data, error } = await supabase
      .from('calendar_blocks')
      .insert({
        date: testDate,
        reason: testReason,
        created_at: new Date().toISOString()
      })
      .select()
      .maybeSingle();

    console.log('✅ Fecha de prueba insertada:', data);
    
    if (error) {
      console.error('❌ Error al insertar:', error);
      throw error;
    }

    // Verificar que se puede leer inmediatamente
    const { data: readData, error: readError } = await supabase
      .from('calendar_blocks')
      .select('*')
      .eq('date', testDate)
      .maybeSingle();

    console.log('🔍 Verificación de lectura inmediata:', readData);
    
    if (readError) {
      console.error('❌ Error al leer fecha recién insertada:', readError);
    }
  } catch (err) {
    console.error('❌ Error en insertTestDate:', err);
    throw err;
  }
}
